import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service_type: "",
  budget: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.service_type ||
      !form.message.trim()
    ) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Demande envoyée — je reviens vers vous sous 24h.");
      setForm(initial);
      setDone(true);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Une erreur est survenue. Réessayez ou contactez-moi par email."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-radial-violet opacity-50 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5 reveal">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-violet-400">
              // Contact
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Parlons de votre{" "}
              <span className="text-violet-400">build idéal</span>.
            </h2>
            <p className="mt-5 text-zinc-400 max-w-md">
              Décrivez votre besoin (usage, budget, contraintes) et je reviens
              vers vous sous 24h avec un devis clair et écrit.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="mailto:industrieszf@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-violet-500/40 bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                  <Mail className="h-5 w-5 text-violet-300" />
                </span>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                    Email
                  </p>
                  <p className="text-sm text-white group-hover:text-violet-300 transition-colors">
                    industrieszf@gmail.com
                  </p>
                </div>
              </a>

              <a
                href="tel:+33780503232"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-violet-500/40 bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                  <Phone className="h-5 w-5 text-violet-300" />
                </span>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                    Téléphone
                  </p>
                  <p className="text-sm text-white group-hover:text-violet-300 transition-colors">
                    07 80 50 32 32
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-violet-500/40 bg-violet-500/10">
                  <MapPin className="h-5 w-5 text-violet-300" />
                </span>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                    Zone d'intervention
                  </p>
                  <p className="text-sm text-white">Île-de-France</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    Atelier · 13 Rue Paul Demange, 92360 Meudon
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="rounded-2xl gradient-border p-6 sm:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-zinc-300 text-xs">
                    Nom*
                  </Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jean Dupont"
                    className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-zinc-300 text-xs">
                    Email*
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jean@email.com"
                    className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300 text-xs">
                    Téléphone*
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">
                    Type de service*
                  </Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      data-testid="form-service"
                      className="mt-2 bg-zinc-900/60 border-white/10 text-white"
                    >
                      <SelectValue placeholder="Choisir une formule" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="pose_seule">Pose seule</SelectItem>
                      <SelectItem value="cle_en_main">PC clé en main</SelectItem>
                      <SelectItem value="nettoyage">Nettoyage complet PC gamer</SelectItem>
                      <SelectItem value="domicile">Intervention à domicile</SelectItem>
                      <SelectItem value="diagnostic">
                        Diagnostic / dépannage
                      </SelectItem>
                      <SelectItem value="upgrade">Upgrade composants</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="budget" className="text-zinc-300 text-xs">
                    Budget approximatif
                  </Label>
                  <Input
                    id="budget"
                    data-testid="form-budget"
                    value={form.budget}
                    onChange={(e) => update("budget", e.target.value)}
                    placeholder="ex : 1500€ — ou « pose seule »"
                    className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-zinc-300 text-xs">
                    Message*
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="form-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={5}
                    placeholder="Usage prévu, jeux/logiciels, contraintes esthétiques (RGB, format), délai souhaité…"
                    className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-60 px-6 py-3 text-sm font-semibold text-white shadow-violet transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>

              {done && (
                <p
                  data-testid="form-success"
                  className="mt-4 text-sm text-violet-300"
                >
                  Demande reçue ! Je reviens vers vous sous 24h max.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
